/**
 * Widget POI config grammar + effective-config resolution — Step 3 of
 * `.claude/plans/REGISTER_LINK_DESIGNATION.md`.
 *
 * PURE module: no I/O, no store, no viewer. It answers one question deterministically —
 * "given a widget's saved POI settings and the register's link declarations, what (if anything)
 * should be highlighted, over what context, in which paint mode?" The impure half — fetching a
 * widget's register declarations — lives in the grid; THIS file is the step's verifiable core and
 * is covered by tracing inputs, no UI required.
 *
 * TRI-STATE, presence-aware (the gate's requirement — `??` cannot tell these apart). A widget key
 * is one of:
 *   ABSENT   → legacy / OFF. This is what keeps every widget saved before Step 3 byte-identical:
 *              with no key, computeEffectivePoi returns null and the caller keeps the whole-tag
 *              path. Protects all 15 seeded widgets (incl. the 5 already using link3D).
 *   'inherit'→ use the register's declared default (isDefaultHighlight / isDefaultContext column).
 *   'none'   → explicitly off; ignore the register default.
 *   <column> → explicit override; must be a column the register actually declared a link on. A
 *              widget may opt out or override, but it may NEVER invent a link the register did not
 *              declare — an unknown column, or a column with no linkEntityType, resolves OFF.
 * '' / whitespace normalises to 'inherit' so a cleared picker never reads as "off by accident".
 */
import { POI_ENTITY_TYPES, DEFAULT_KEY_FIELD } from './poiResolvers';

/** Sentinel widget-config values (NOT column names). */
export const POI_SOURCE_INHERIT = 'inherit';
export const POI_SOURCE_NONE = 'none';

/** Paint modes a widget may carry. 'additive' overlays the highlight; 'dim' greys the context. */
export const POI_PAINT_MODE = Object.freeze({ ADDITIVE: 'additive', DIM: 'dim' });

/** The entity types a link may resolve to (the resolver-backed set from the Step-2 registry). */
const VALID_ENTITY_TYPES = new Set(Object.values(POI_ENTITY_TYPES));

/**
 * Capability by DECLARED entity type — known without geometry (plan §1/§4), so the Step-4 picker can
 * offer/disable options straight from the register's declarations. A column may drive the 3D
 * HIGHLIGHT only if it resolves to a narrow anchor (`cml`, `anomalyTag`); `inspectionTag` /
 * `masterTag` cover the whole asset (context only), and `document` has no geometry (reference only).
 * CONTEXT — the backdrop a highlight is dimmed over — accepts the whole-asset tag types AND the
 * highlight-capable ones. Frozen arrays are truly immutable; membership is a small `.includes`.
 */
export const POI_HIGHLIGHT_TYPES = Object.freeze([
  POI_ENTITY_TYPES.CML,
  POI_ENTITY_TYPES.ANOMALY_TAG,
]);
export const POI_CONTEXT_TYPES = Object.freeze([
  POI_ENTITY_TYPES.INSPECTION_TAG,
  POI_ENTITY_TYPES.MASTER_TAG,
  POI_ENTITY_TYPES.CML,
  POI_ENTITY_TYPES.ANOMALY_TAG,
]);

/**
 * Why a declared column is NOT offered as a highlight — surfaced (disabled) in the highlight picker
 * so the user gets a reason instead of a silently-missing option (plan §4). Keyed by linkEntityType.
 */
export const POI_HIGHLIGHT_DISABLED_REASON = Object.freeze({
  [POI_ENTITY_TYPES.INSPECTION_TAG]: "covers the whole asset — can't narrow",
  [POI_ENTITY_TYPES.MASTER_TAG]: "covers the whole asset — can't narrow",
  [POI_ENTITY_TYPES.DOCUMENT]: 'no 3D geometry — can only be opened',
});

/**
 * True when a register column actually declares a resolvable link (a valid linkEntityType). The
 * picker offers only declared links; a register that declares none shows no picker at all.
 * @param {{linkEntityType:?string}} decl
 * @returns {boolean}
 */
export function isPoiLinkColumn(decl) {
  return !!decl && VALID_ENTITY_TYPES.has(decl.linkEntityType);
}

/**
 * Normalise a raw widget-config source value to the presence-aware tri-state.
 * @param {*} raw - the stored value of poiSource / contextSource / poiPaintMode
 * @returns {string|undefined} undefined when ABSENT (→ OFF); 'inherit' for ''/whitespace/'inherit';
 *   otherwise the trimmed value ('none', a column name, or a paint mode).
 */
export function normalizePoiSource(raw) {
  if (raw === undefined || raw === null) return undefined; // key absent → legacy / OFF
  const s = String(raw).trim();
  if (s === '') return POI_SOURCE_INHERIT; // cleared picker → inherit, never OFF by accident
  return s;
}

/**
 * Which colour definition governs a widget's 3D paint, highlight and legend.
 *
 * A widget can carry TWO unrelated colour definitions at once — the chart's series colour SCHEMA (a
 * facility-wide named registry) and the PROPERTY colours (`Colour models by`, an inline map generated
 * from the column's own distinct values). Before this key existed neither was authoritative, so a
 * chart could paint `Low` green while the 3D beside it painted `Low` orange. They cannot be merged:
 * a chart's grouping column and its colouring column may legitimately differ, so there is no single
 * map to unify onto — hence an explicit, stored winner.
 */
export const HIGHLIGHT_COLOR_SOURCE = Object.freeze({ SCHEMA: 'schema', PROPERTY: 'property' });

/**
 * Normalise the stored `highlightColorSource`. Total and presence-aware: ABSENT stays absent so
 * widgets saved before this control existed keep painting exactly as they do today (an unset key must
 * never be silently reinterpreted as a choice the user never made).
 * @param {*} raw - the stored value of highlightColorSource
 * @returns {'schema'|'property'|null} null when absent, unrecognised, or blank → caller keeps legacy
 */
export function normalizeHighlightColorSource(raw) {
  if (raw === undefined || raw === null) return null; // key absent → legacy behaviour
  const s = String(raw).trim().toLowerCase();
  if (s === HIGHLIGHT_COLOR_SOURCE.SCHEMA) return HIGHLIGHT_COLOR_SOURCE.SCHEMA;
  if (s === HIGHLIGHT_COLOR_SOURCE.PROPERTY) return HIGHLIGHT_COLOR_SOURCE.PROPERTY;
  return null; // unknown value → legacy, never a guess
}

/**
 * Pick the declaration a normalised source resolves to, or null.
 * @param {Array<object>} decls - the register's column declarations (client shape)
 * @param {string|undefined} source - a normalised source from normalizePoiSource
 * @param {'highlight'|'context'} role - which default flag 'inherit' reads
 * @returns {object|null} the chosen declaration, or null when nothing resolves
 */
function resolveDeclaration(decls, source, role) {
  if (source === undefined || source === POI_SOURCE_NONE) return null; // absent or explicit off
  const defaultFlag = role === 'highlight' ? 'isDefaultHighlight' : 'isDefaultContext';
  // Match on displayName FIRST — that is the physical view column the query, the grid rows and the
  // forced `columns` all use; the register's internal `name` (field_1…) is not a real column and
  // selecting it throws "Invalid column: field_1". name is accepted as a tolerant fallback.
  const chosen = source === POI_SOURCE_INHERIT
    ? decls.find((c) => c && c[defaultFlag] === true)
    : decls.find((c) => c && (c.displayName === source || c.name === source));
  if (!chosen) return null;
  // A widget may never invent a link the register did not declare, and a typo'd/unknown type is
  // not a link — either way the config resolves OFF rather than pointing at nothing.
  if (!VALID_ENTITY_TYPES.has(chosen.linkEntityType)) return null;
  return chosen;
}

/**
 * The field of the target entity a column is matched against. The register's declared linkKeyField
 * wins; otherwise the Step-2 per-type default (`cml`→CmlNo, tags→Name, `document`→DocumentTitle).
 * It is AUTHORING metadata: the resolved key field is persisted with the designation and read back
 * by the server's spatial resolver. Client-side resolution no longer exists — `SUPPORTED_KEY_FIELDS`
 * is what keeps a designation from being authored with a field the server could never match on.
 * @param {{linkEntityType:string, linkKeyField:?string}} decl
 * @returns {string|null}
 */
function keyFieldFor(decl) {
  const explicit = decl.linkKeyField;
  if (explicit !== undefined && explicit !== null && String(explicit).trim() !== '') {
    return String(explicit).trim();
  }
  return DEFAULT_KEY_FIELD[decl.linkEntityType] ?? null;
}

/**
 * Paint mode is a widget-only concern (the register declares no default — plan §3), so 'inherit'
 * has nothing to inherit and resolves to null, and the consumer applies its own default. absent /
 * 'none' / anything unrecognised → null.
 * @param {*} raw
 * @returns {string|null} 'additive' | 'dim' | null
 */
function resolvePaintMode(raw) {
  const s = normalizePoiSource(raw);
  if (s === POI_PAINT_MODE.ADDITIVE || s === POI_PAINT_MODE.DIM) return s;
  return null;
}

/**
 * Resolve a widget's saved POI config against a register's link declarations. PURE.
 *
 * @param {object} widgetConfig - the parsed sqlViewConfig (reads poiSource / contextSource /
 *   poiPaintMode; every other key ignored).
 * @param {Array<{name:string, linkEntityType:?string, linkKeyField:?string,
 *   isDefaultHighlight:?boolean, isDefaultContext:?boolean}>} registerDeclarations - the register's
 *   columns in client (camelCase) shape. Anything not an array is treated as "no declarations".
 * @returns {{ highlightColumn:string, highlightType:string, highlightKeyField:string|null,
 *   contextColumn:string|null, contextType:string|null, paintMode:string|null } | null}
 *   null when no highlight is effective → the caller keeps today's whole-tag path. A context with
 *   no highlight is not "effective" on its own (context is the backdrop a highlight is shown over),
 *   so that too returns null.
 */
export function computeEffectivePoi(widgetConfig, registerDeclarations) {
  const decls = Array.isArray(registerDeclarations) ? registerDeclarations : [];
  const cfg = widgetConfig || {};

  const highlight = resolveDeclaration(decls, normalizePoiSource(cfg.poiSource), 'highlight');
  if (!highlight) return null; // no effective highlight → whole-tag path (today's behaviour)

  const context = resolveDeclaration(decls, normalizePoiSource(cfg.contextSource), 'context');

  // Return the DISPLAY name — the physical view column that the dashboard query, the grid row keys
  // and the forced `columns` list all use. The internal `name` (field_1…) is not a selectable column.
  return {
    highlightColumn: highlight.displayName || highlight.name,
    highlightType: highlight.linkEntityType,
    highlightKeyField: keyFieldFor(highlight),
    contextColumn: context ? (context.displayName || context.name) : null,
    contextType: context ? context.linkEntityType : null,
    paintMode: resolvePaintMode(cfg.poiPaintMode),
  };
}
